import {Injectable, Inject, PLATFORM_ID, OnDestroy} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {environment} from '../../environments/environment';
import {SwUpdate, VersionReadyEvent} from '@angular/service-worker';
import {MatSnackBar} from '@angular/material/snack-bar';
import {NavigationEnd, Router} from '@angular/router';
import {filter} from 'rxjs/operators';
import {interval, Subscription} from 'rxjs';
import * as Sentry from '@sentry/angular';

@Injectable({
  providedIn: 'root'
})
export class VersionService implements OnDestroy {

  private static readonly CHECK_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes
  // How long an ignored update is allowed to sit before we silently apply it
  // the next time the tab is in the background (invisible to the user).
  private static readonly FORCE_UPDATE_GRACE_MS = 2 * 60 * 60 * 1000; // 2 hours
  // Floor between two checkForUpdate() calls, so rapid tab-switching doesn't
  // fire a network request on every single visibilitychange.
  private static readonly MIN_CHECK_GAP_MS = 5 * 60 * 1000; // 5 minutes

  private updateShown = false;
  private versionReadyAt: number | null = null;
  private lastCheckAt = 0;
  private forceUpdateInFlight = false;
  private readonly onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      this.forceUpdateIfOverdue();
    } else {
      this.checkForUpdate();
    }
  };
  private readonly onOnline = () => this.checkForUpdate();
  private navigationSubscription: Subscription | null = null;

  constructor(
    private swUpdate: SwUpdate,
    private snackBar: MatSnackBar,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.listenForUpdates();
    this.listenForUnrecoverableState();
    this.setupPeriodicChecks();
    this.setupActivityBasedChecks();
    this.setupNavigationChecks();
  }

  private listenForUpdates(): void {
    this.swUpdate.versionUpdates
      .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
      .subscribe(() => {
        if (!environment.production) {
          console.log('[VersionService] New version ready');
        }
        this.versionReadyAt ??= Date.now();
        this.showUpdateSnackbar();
      });
  }

  private showUpdateSnackbar(): void {
    if (this.updateShown) {
      return;
    }
    this.updateShown = true;

    const snackRef = this.snackBar.open(
      $localize`:@@version.update-available:Na voljo je nova različica aplikacije`,
      $localize`:@@version.update-action:Posodobi`,
      {
        duration: 0,
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
        panelClass: ['bg-primary', 'text-white'],
      }
    );

    snackRef.onAction().subscribe(() => {
      this.swUpdate.activateUpdate()
        .catch((err) => {
          // activateUpdate() failing shouldn't strand the user on a dead
          // click — a plain reload still has a good chance of picking up
          // the new service worker on next load.
          Sentry.captureException(err);
        })
        .finally(() => this.reloadPage());
    });

    snackRef.afterDismissed().subscribe(() => {
      this.updateShown = false;
    });
  }

  private listenForUnrecoverableState(): void {
    this.swUpdate.unrecoverable.subscribe((event) => {
      if (!environment.production) {
        console.error('[VersionService] Unrecoverable SW state:', event.reason);
      }
      this.reloadPage();
    });
  }

  private setupPeriodicChecks(): void {
    if (!environment.production) {
      return;
    }
    interval(VersionService.CHECK_INTERVAL_MS).subscribe(() => this.checkForUpdate());
  }

  /**
   * The periodic interval alone misses long-lived tabs: browsers throttle or
   * fully suspend timers in background/idle tabs, so a machine left open for
   * days can go without ever re-checking. Re-check whenever the tab actually
   * becomes relevant again, and silently apply an update that's been sitting
   * ignored for a while at the moment the tab goes to the background — where
   * a reload is invisible instead of disruptive.
   */
  private setupActivityBasedChecks(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('online', this.onOnline);
  }

  /**
   * A stale shell only becomes user-visible when navigation asks it to load
   * a lazy chunk the server no longer has (see GlobalErrorHandler's
   * "Loading chunk N failed" recovery). NavigationEnd fires after that chunk
   * has already resolved (or failed), so this can't save the navigation that
   * triggers it — but it keeps detection warm for whichever navigation comes
   * next, since users navigate far more often than the 30-min/visibility
   * checks alone would catch.
   */
  private setupNavigationChecks(): void {
    this.navigationSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => this.checkForUpdate());
  }

  ngOnDestroy(): void {
    this.navigationSubscription?.unsubscribe();

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('online', this.onOnline);
  }

  private checkForUpdate(): void {
    if (Date.now() - this.lastCheckAt < VersionService.MIN_CHECK_GAP_MS) {
      return;
    }
    this.lastCheckAt = Date.now();
    this.swUpdate.checkForUpdate().catch((err) => Sentry.captureException(err));
  }

  private forceUpdateIfOverdue(): void {
    if (this.forceUpdateInFlight || this.versionReadyAt === null) {
      return;
    }
    if (Date.now() - this.versionReadyAt < VersionService.FORCE_UPDATE_GRACE_MS) {
      return;
    }

    this.forceUpdateInFlight = true;
    this.swUpdate.activateUpdate()
      // Reload either way — the tab is hidden, so it's invisible regardless
      // of outcome, and a failed activation just leaves the SW cache as-is
      // for the next check to retry. Logged so a persistently failing SW
      // doesn't fail silently forever.
      .catch((err) => Sentry.captureException(err))
      .finally(() => {
        this.forceUpdateInFlight = false;
        this.reloadPage();
      });
  }

  private reloadPage(): void {
    if (isPlatformBrowser(this.platformId)) {
      window.location.reload();
    }
  }
}
